<script setup lang="ts">
  import Vue3Datatable from "@bhplugin/vue3-datatable";
  import Lucide from "@/base-components/Lucide/Lucide.vue";
  import { useAggregateList } from "@/composables/useAggregateList";
  import moment from "moment-jalaali";
  import { useI18n } from "vue-i18n";
  import { onMounted, ref } from "vue";

  const { t: $t } = useI18n();

  const { aggregateList, aggregateLoadingFlag, aggregateInitialize } =
    useAggregateList();
  const props = defineProps<{
    adminMode?: boolean;
    rows: any[];
    loading: boolean;
    pageOptions: { page: number; count: number; limit: number };
  }>();
  const emits = defineEmits(["rowClick"]);

  function rowClick(data: any) {
    emits("rowClick", data);
  }

  const cols = props.adminMode
    ? ref([
        { field: "counter", title: "#" },
        { field: "country", title: $t("title.regions") },
        { field: "name", title: $t("title.server-name") },
        { field: "hostname", title: $t("title.hostname") },
        { field: "Interfaces", title: $t("title.ip") },
        { field: "Volumes", title: $t("title.volume") },
        { field: "InstanceStatus", title: $t("title.instance-status") },
        { field: "os", title: $t("title.os-type") },
        { field: "paymentCycle", title: $t("title.payment-cycle") },
        { field: "owner", title: $t("title.user") },
        { field: "creator", title: $t("title.creator") },
        { field: "createdAt", title: $t("title.created-at") },
        { field: "ServiceStatus", title: $t("title.service-status") },
      ])
    : ref([
        { field: "counter", title: "#" },
        { field: "id", title: $t("title.id") },
        { field: "country", title: $t("title.regions") },
        { field: "name", title: $t("title.server-name") },
        { field: "hostname", title: $t("title.hostname") },
        { field: "Interfaces", title: $t("title.ip") },
        { field: "Volumes", title: $t("title.volume") },
        { field: "InstanceStatus", title: $t("title.instance-status") },
        { field: "os", title: $t("title.os-type") },
        { field: "ServiceStatus", title: $t("title.service-status") },
      ]);

  function getVmStatus(buildStatus: number, powerStatus: number) {
    const BuildStatus = {
      0: "not-build",
      1: "building",
      2: "built",
      3: "build-error",
    };

    const PowerStatus = {
      0: "pending",
      1: "running",
      3: "paused",
      4: "shutdown",
      6: "crashed",
      7: "suspended",
    };

    if (buildStatus !== 2) {
      const str = BuildStatus[buildStatus] || "unknown";
      return $t(`title.${str}`);
    }

    const str = PowerStatus[powerStatus] || "unknown";
    return $t(`title.${str}`);
  }

  function getVmIcon(buildStatus: number, powerStatus: number) {
    if (buildStatus !== 2) {
      if (buildStatus === 0)
        return ["EllipsisIcon", "animate-bounce text-red-500 inline-block"];
      if (buildStatus === 1)
        return ["Loader", "animate-spin text-yellow-500 inline-block"];
      if (buildStatus === 3)
        return ["CircleAlert", "animate-spin text-red-700 inline-block"];
    }
    if (powerStatus === 0)
      return ["ScreenShareOff", "text-gray-500 inline-block"];
    if (powerStatus === 1)
      return ["MonitorPlay", "text-green-800 inline-block"];
    if (powerStatus === 3)
      return ["MonitorPause", "text-yellow-500 inline-block"];
    if (powerStatus === 4) return ["MonitorStop", "text-black inline-block"];
    if (powerStatus === 6) return ["MonitorOff", "text-red-600 inline-block"];
    if (powerStatus === 7)
      return ["MonitorCog", "text-orange-600 inline-block"];

    return ["AlertCircle", "text-red-700 inline-block"];
  }

  function getServiceStatus(status: number) {
    const statusList = {
      0: "draft",
      2: "activeRequest",
      5: "active",
      10: "suspend",
      [-1]: "delete",
    };
    const txt = statusList[status] || "unknown";
    return $t(`title.${txt}`);
  }

  function getServiceStatusColor(status: number) {
    if (status === 0) return "text-gray-500 inline-block";
    if (status === 5) return "text-green-700 inline-block animate-pulse";
    if (status === 2) return "text-orange-500 inline-block animate-pulse";
    if (status === 10) return "text-red-500 inline-block";
    if (status === -1) return "text-black inline-block";
    return "text-purple-500 inline-block";
  }

  function getRegionId(data: any) {
    return data.value?.regionId || data.value?.Region?.id || "";
  }

  function getRegion(data: any) {
    return (
      aggregateList.value[data.value?.regionId || data.value?.Region?.id] ??
      undefined
    );
  }

  const tagHelper = (tags: string[]) => {
    return tags.reduce((acc: Record<string, string>, cur: string) => {
      const data = cur.split(":");
      acc[data.shift() || cur] = data.join(":");
      return acc;
    }, {});
  };

  function getOsLogo(data: any) {
    try {
      const vol = data.value.Volumes.find((x) => !!x.imageExternalId);
      const regionData =
        aggregateList.value[data.value?.regionId || data.value?.Region?.id] ??
        undefined;
      const image = regionData.images.find((x) => x.id === vol.imageExternalId);
      const tags = tagHelper(image.tags);
      const name = (tags.name || image.name).toLowerCase();
      return `/image/icons/os/${name}.svg`;
    } catch {}
    return "";
  }

  onMounted(async () => {
    await aggregateInitialize();
  });
</script>

<template>
  <vue3-datatable
    :columns="cols"
    :rows="props.rows"
    :loading="props.loading"
    :totalRows="props.pageOptions.count"
    :isServerMode="true"
    :pageSize="props.pageOptions.limit"
    :pagination="false"
    :sortable="false"
    class="advanced-table px-4 text-start items-start justify-start"
    rowClass="cursor-pointer"
    @rowClick="rowClick"
  >
    <!-- Empty header slot to remove the header -->
    <template #header>
      <!-- No content here will hide the header -->
    </template>
    <!-- Empty footer slot to remove the footer -->
    <template #footer>
      <!-- No content here will hide the footer -->
    </template>

    <template #counter="data, key">
      <div class="text-start">
        {{
          props.rows.indexOf(data.value) +
          1 +
          (props.pageOptions.page - 1) * props.pageOptions.limit
        }}
      </div>
    </template>
    <template #id="data, key">
      <div class="text-start">
        {{ data.value.id }}
      </div>
    </template>
    <template #country="data, key">
      <div class="text-start flex pe-4 gap-3">
        <img
          v-if="getRegion(data)?.flag"
          :src="getRegion(data)?.flag"
          :alt="`Flag of ${getRegion(data)?.displayName}`"
          class="flag-icon w-5 h-5"
        />
        <div class="">
          {{ getRegion(data)?.displayName ?? "-" }}
        </div>
      </div>
    </template>
    <template #name="data, key">
      <div class="text-start">
        {{ data.value.name }}
      </div>
    </template>
    <template #hostname="data, key">
      <div class="text-start">
        {{ data.value.hostname }}
      </div>
    </template>
    <template #Interfaces="data, key">
      <div class="text-start">
        {{ data.value.Interfaces.map((data) => data.ip ?? "-").join(",") }}
      </div>
    </template>
    <template #Volumes="data, key">
      <div class="text-start">
        {{
          data.value.Volumes.map((data) => `${data.name} (${data.type}) `).join(
            ", "
          )
        }}
      </div>
    </template>
    <template #os="data, key">
      <div class="text-left flex gap-3" v-if="!aggregateLoadingFlag" dir="rtl">
        <img :src="getOsLogo(data)" class="flag-icon w-5 h-5" alt="" />
        {{
          getRegion(data)
            ? getRegion(data)[
                `${getRegionId(data)}-${
                  data.value.Volumes[0]?.imageExternalId
                }-name`
              ]
            : ""
        }}
      </div>
    </template>
    <template #ServiceStatus="data, key">
      <div class="text-start">
        <Lucide
          v-if="data.value.status === 2"
          icon="Loader"
          class="animate-spin text-yellow-500 inline-block"
        />
        <Lucide
          v-else
          :icon="data.value.status !== -1 ? 'Activity' : 'PackageX'"
          :class="getServiceStatusColor(data.value.status)"
        />
        <span class="mr-1">{{ getServiceStatus(data.value.status) }}</span>
      </div>
    </template>
    <template #InstanceStatus="data, key">
      <div class="text-start">
        <Lucide
          :icon="getVmIcon(data.value.buildStatus, data.value.powerStatus)[0]"
          :class="getVmIcon(data.value.buildStatus, data.value.powerStatus)[1]"
        />
        <span class="mr-1">{{
          getVmStatus(data.value.buildStatus, data.value.powerStatus)
        }}</span>
      </div>
    </template>
    <template #owner="data, key">
      <div class="text-start">
        {{ data.value.Owner?.Profile?.firstName }}
        {{ data.value.Owner?.Profile?.lastName }}
      </div>
    </template>
    <template #paymentCycle="data, key">
      <div class="text-start">
        {{ data.value.paymentCycle }} {{ $t("title.month") }}
      </div>
    </template>
    <template #creator="data, key">
      <div class="text-start">
        {{ data.value.Creator?.Profile?.firstName }}
        {{ data.value.Creator?.Profile?.lastName }}
      </div>
    </template>
    <template #createdAt="data, key">
      <div class="text-start">
        {{
          data.value.createdAt
            ? moment(data.value.createdAt)
                .locale("fa")
                .format("jYYYY/jMM/jDD HH:mm")
            : "-"
        }}
      </div>
    </template>
  </vue3-datatable>
</template>

<style scoped></style>
